/**
 * `Bus/Search` `Vehicles[]` item helpers — the real Amadeus response uses Turkish field
 * names and has NO company-name field (only `segmentLogoUrl`) and no flat price (only
 * `priceDetails`). These readers keep that quirk in one place for `BusFlowPage` (results
 * list) and `flowConfig.ts` (schema/allocate trip identity).
 */
import type { BusVehicle } from '../../api/types/bus';

/** `.../BusCompanies/metro-turizm.png` → "Metro Turizm". */
export function busCompanyNameFromLogo(url: string | undefined): string {
  if (!url) return '';
  const slug = (url.split('/').pop() ?? '').replace(/\.[a-z0-9]+$/i, '');
  if (!slug) return '';
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toLocaleUpperCase('tr') + w.slice(1))
    .join(' ');
}

/** `BusSchemaRequest.journeyNo` / `BusAllocatedDirection.journeyNo`. */
export function busVehicleJourneyNo(trip: BusVehicle | undefined): string {
  const legacy = typeof trip?.journeyNo === 'string' ? trip.journeyNo : '';
  return trip?.seferNo || legacy || '';
}

/** `Bus/GetBusSchema` + `Bus/Allocate` `journeyDate` — the journey **date at midnight**
 *  (`YYYY-MM-DDT00:00:00`), NOT the boarding time. Legacy `Result.cshtml` strips
 *  `BinisArayolDate` to Y/M/D and rebuilds at 00:00; a real allocate request confirmed
 *  `"2026-09-15T00:00:00"`. `fallback` is the search form's `YYYY-MM-DD`. */
export function busVehicleJourneyDate(trip: BusVehicle | undefined, fallback = ''): string {
  const legacy = typeof trip?.journeyDate === 'string' ? trip.journeyDate : '';
  const raw = trip?.binisArayolDate || legacy || fallback;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
  return m ? `${m[1]}T00:00:00` : raw;
}

/** Displayed price — provider sends it nested in `priceDetails`. */
export function busVehiclePrice(trip: BusVehicle | undefined): number | undefined {
  const p = trip?.priceDetails;
  const flat = typeof trip?.price === 'number' ? trip.price : undefined;
  return p?.displayPrice ?? p?.userPrice ?? p?.systemPrice ?? flat;
}

/** One-line label for the results list. */
export function busVehicleLabel(trip: BusVehicle): string {
  const company = busCompanyNameFromLogo(trip.segmentLogoUrl) || 'Otobüs firması';
  const route = [trip.binisDurak, trip.inisDurak].filter(Boolean).join(' → ');
  const time = trip.binisSaati ? ` (${trip.binisSaati})` : '';
  const dur = trip.sure ? ` · ${trip.sure}` : '';
  return `${company} — ${route}${time}${dur}`.trim();
}
